import { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  KeyRound, Save, ShieldCheck, Loader2, AlertTriangle, Eye, EyeOff,
  Building2, FileSignature, Plus, Pencil, Trash2, Users,
} from "lucide-react";

const emptyLegal = {
  rna: "", siren: "", siret: "", declaration_prefecture: "", declaration_date: "",
  siege_address: "", president_name: "", vice_president_name: "",
  treasurer_name: "", secretary_name: "",
  iban: "", bic: "", bank_name: "",
};

const ROLE_PRESETS = ["Président", "Vice-Président", "Trésorier", "Secrétaire", "Membre du bureau"];

export default function Parametres() {
  const [codes, setCodes] = useState([]);
  const [legal, setLegal] = useState(emptyLegal);
  const [savingLegal, setSavingLegal] = useState(false);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", code: "" });
  const [showCode, setShowCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadCodes = () => api.get("/auth/codes").then((r) => setCodes(r.data)).catch(() => {});
  const loadLegal = () => api.get("/settings/legal").then((r) => setLegal({ ...emptyLegal, ...r.data })).catch(() => {});

  useEffect(() => { loadCodes(); loadLegal(); }, []);

  const openNew = () => { setEditing(null); setForm({ label: "", code: "" }); setShowCode(false); setDlgOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ label: c.label, code: "" }); setShowCode(false); setDlgOpen(true); };

  const submit = async () => {
    if (!form.label.trim()) { toast.error("Le libellé est requis."); return; }
    if (!editing && form.code.trim().length < 4) { toast.error("Le code doit contenir au moins 4 caractères."); return; }
    if (editing && form.code && form.code.trim().length < 4) { toast.error("Le nouveau code doit contenir au moins 4 caractères."); return; }
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { label: form.label };
        if (form.code) payload.code = form.code;
        await api.put(`/auth/codes/${editing.id}`, payload);
        toast.success("Code mis à jour.");
      } else {
        await api.post("/auth/codes", { label: form.label, code: form.code });
        toast.success(`Code « ${form.label} » ajouté.`);
      }
      setDlgOpen(false); loadCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const remove = async (c) => {
    if (codes.length <= 1) { toast.error("Impossible de supprimer le dernier code."); return; }
    if (!window.confirm(`Supprimer le code de ${c.label} ?`)) return;
    try {
      await api.delete(`/auth/codes/${c.id}`);
      toast.success("Code supprimé.");
      loadCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const saveLegal = async () => {
    setSavingLegal(true);
    try {
      await api.put("/settings/legal", legal);
      toast.success("Informations légales enregistrées.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingLegal(false); }
  };

  return (
    <div data-testid="parametres-page" className="max-w-4xl">
      <PageHeader
        eyebrow="Administration"
        title="Paramètres"
        description="Gérez les codes d'accès des membres du bureau et les informations légales de l'association."
      />

      <Tabs defaultValue="access" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger data-testid="tab-access" value="access"><ShieldCheck size={14} className="mr-2" /> Codes d'accès</TabsTrigger>
          <TabsTrigger data-testid="tab-legal" value="legal"><FileSignature size={14} className="mr-2" /> Informations légales</TabsTrigger>
        </TabsList>

        <TabsContent value="access">
          <div className="bg-white border border-brand-border rounded-xl p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="p-3 rounded-lg bg-brand-navy/5 text-brand-navy">
                <Users size={22} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-2xl text-brand-navy">Codes d'accès du bureau</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Chaque membre du bureau peut avoir son propre code d'accès. Lors de la connexion, le code utilisé identifie automatiquement la personne.
                </p>
              </div>
              <Button data-testid="add-code-btn" onClick={openNew} className="bg-brand-orange hover:bg-[#D04A06] text-white">
                <Plus size={14} className="mr-1" /> Ajouter un code
              </Button>
            </div>

            {codes.some((c) => c.is_default) && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>Un code par défaut (<code className="font-mono">AIJ2026</code>) est encore actif. Modifiez-le ou supprimez-le après avoir créé les codes du bureau.</span>
              </div>
            )}

            <div className="overflow-x-auto border border-brand-border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 small-caps">
                  <tr>
                    <th className="text-left py-3 px-4">Membre / Rôle</th>
                    <th className="text-left py-3 px-4">Code</th>
                    <th className="text-left py-3 px-4">Longueur</th>
                    <th className="text-left py-3 px-4">Statut</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.length === 0 && (
                    <tr><td colSpan={5} className="py-10 text-center text-gray-400">Aucun code défini.</td></tr>
                  )}
                  {codes.map((c) => (
                    <tr key={c.id} data-testid={`code-row-${c.id}`} className="border-t border-brand-border hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-brand-navy">{c.label || "—"}</td>
                      <td className="py-3 px-4 font-mono text-gray-600">{c.hint}</td>
                      <td className="py-3 px-4 text-gray-600">{c.length} car.</td>
                      <td className="py-3 px-4">
                        {c.is_default
                          ? <Badge className="bg-brand-orange text-white">Code par défaut</Badge>
                          : <Badge className="bg-brand-green text-white">Personnalisé</Badge>}
                      </td>
                      <td className="py-3 px-4 text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(c)}><Trash2 size={14} className="text-red-500" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5 text-xs text-gray-500 leading-relaxed">
              <p><strong>Bonnes pratiques</strong> :</p>
              <ul className="list-disc list-inside space-y-0.5 mt-1">
                <li>Donnez un code unique à chaque membre du bureau (Président, Trésorier, etc.).</li>
                <li>Au moins 8 caractères, mélangeant lettres et chiffres.</li>
                <li>Changez les codes après chaque renouvellement du bureau.</li>
                <li>Le code n'est jamais affiché en clair après création — notez-le bien.</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="legal">
          <div className="bg-white border border-brand-border rounded-xl p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="p-3 rounded-lg bg-brand-navy/5 text-brand-navy">
                <Building2 size={22} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-2xl text-brand-navy">Informations légales de l'association</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Apparaissent automatiquement en pied de page des documents PDF.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><div className="small-caps mb-2">Identité juridique</div></div>
              <div><Label>N° RNA</Label><Input data-testid="legal-rna" value={legal.rna} onChange={(e) => setLegal({...legal, rna: e.target.value})} placeholder="W852014125" /></div>
              <div><Label>N° SIREN</Label><Input data-testid="legal-siren" value={legal.siren} onChange={(e) => setLegal({...legal, siren: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>N° SIRET</Label><Input data-testid="legal-siret" value={legal.siret} onChange={(e) => setLegal({...legal, siret: e.target.value})} /></div>
              <div><Label>N° de déclaration en préfecture</Label><Input value={legal.declaration_prefecture} onChange={(e) => setLegal({...legal, declaration_prefecture: e.target.value})} /></div>
              <div><Label>Date de déclaration</Label><Input type="date" value={legal.declaration_date || ""} onChange={(e) => setLegal({...legal, declaration_date: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Adresse du siège social (si différente)</Label><Input value={legal.siege_address} onChange={(e) => setLegal({...legal, siege_address: e.target.value})} /></div>

              <div className="md:col-span-2 border-t border-brand-border pt-4 mt-2"><div className="small-caps mb-2">Bureau de l'association</div></div>
              <div><Label>Président(e)</Label><Input value={legal.president_name} onChange={(e) => setLegal({...legal, president_name: e.target.value})} /></div>
              <div><Label>Vice-Président(e)</Label><Input value={legal.vice_president_name} onChange={(e) => setLegal({...legal, vice_president_name: e.target.value})} /></div>
              <div><Label>Trésorier(ère)</Label><Input value={legal.treasurer_name} onChange={(e) => setLegal({...legal, treasurer_name: e.target.value})} /></div>
              <div><Label>Secrétaire</Label><Input value={legal.secretary_name} onChange={(e) => setLegal({...legal, secretary_name: e.target.value})} /></div>

              <div className="md:col-span-2 border-t border-brand-border pt-4 mt-2"><div className="small-caps mb-2">Coordonnées bancaires</div></div>
              <div className="md:col-span-2"><Label>Nom de la banque</Label><Input value={legal.bank_name} onChange={(e) => setLegal({...legal, bank_name: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>IBAN</Label><Input value={legal.iban} onChange={(e) => setLegal({...legal, iban: e.target.value})} placeholder="FR76 ..." /></div>
              <div><Label>BIC</Label><Input value={legal.bic} onChange={(e) => setLegal({...legal, bic: e.target.value})} /></div>
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-brand-border">
              <Button data-testid="save-legal-btn" onClick={saveLegal} disabled={savingLegal} className="bg-brand-orange hover:bg-[#D04A06] text-white">
                {savingLegal ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: add / edit code */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="code-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-navy">
              {editing ? "Modifier le code" : "Nouveau code d'accès"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1">Membre / Rôle *</Label>
              <Input
                data-testid="code-label-input"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Président, Trésorier, ..."
                list="role-presets"
              />
              <datalist id="role-presets">
                {ROLE_PRESETS.map((r) => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-1">
                <KeyRound size={14} /> {editing ? "Nouveau code (optionnel)" : "Code d'accès *"}
              </Label>
              <div className="relative">
                <Input
                  data-testid="code-value-input"
                  type={showCode ? "text" : "password"}
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder={editing ? "Laisser vide pour ne pas changer" : "8 caractères minimum recommandé"}
                  className="pr-10 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowCode((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCode ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {!editing && (
                <p className="text-xs text-gray-400 mt-1">
                  Notez ce code, il ne sera plus affiché en clair après création.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Annuler</Button>
            <Button data-testid="save-code-btn" onClick={submit} disabled={submitting} className="bg-brand-orange hover:bg-[#D04A06] text-white">
              {submitting ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />}
              {editing ? "Enregistrer" : "Créer le code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
